import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, IRowNode } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { CustomButton } from "../../../components/common/customButton";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { exportStatusAnalysisToExcel } from "../../../services/summaryExportService";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import type { Status } from "../../../types/deal";

ModuleRegistry.registerModules([AllCommunityModule]);

type StatusHistoryEntry = {
  status_id: number | null;
  status_name: string | null;
  date_changed?: string | null;
  changed_at?: string | null;
  days_in_status?: number | null;
  duration_days?: number | null;
};

type DealStatusHistory = {
  created_date: string;
  deal_id: number;
  deal_name: string | null;
  status_history: StatusHistoryEntry[];
};

type StatusDurationRow = {
  key: string;
  statusId: number | null;
  statusName: string;
  deals: number;
  avgDays: number;
};

type DealMatrixRow = {
  dealId: number;
  dealName: string;
  statusDates: Record<string, string>;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const toValidDate = (value: unknown): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (iso: string): string => {
  const d = toValidDate(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(d);
};

const isHiddenOnLeft = (statusName: string): boolean => {
  const s = statusName.trim().toLowerCase();
  return (
    s.includes("killed") ||
    s.includes("closed") ||
    s.includes("lost to other lender")
  );
};

export function StatusAnalysisTab() {
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const statusIds = useMemo(
    () => statusFilter.map((s) => s.id),
    [statusFilter],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<DealStatusHistory[] | null>(null);
  const [selectedStatusKey, setSelectedStatusKey] = useState<string | null>(
    null,
  );
  const [dealGridApi, setDealGridApi] = useState<any>(null);

  const { status: statusList } = useReferenceLists();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    setIsLoading(true);
    apiGet<DealStatusHistory[]>("status-history")
      .then((response) => {
        if (response.success && response.data) {
          setData(response.data);
        } else {
          showErrorFromResponse(response);
          setData([]);
        }
      })
      .catch(() => {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch status history. Please try again.",
        });
        setData([]);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOrderById = useMemo(() => {
    const m = new Map<number, number>();
    for (const s of statusList ?? []) {
      const id = (s as any)?.id;
      const sortOrder = (s as any)?.sort_order;
      if (typeof id === "number") {
        m.set(id, typeof sortOrder === "number" ? sortOrder : 9999);
      }
    }
    return m;
  }, [statusList]);

  const statusNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of statusList ?? []) {
      const id = (s as any)?.id;
      const name = (s as any)?.status;
      if (
        typeof id === "number" &&
        typeof name === "string" &&
        name.trim() !== ""
      ) {
        m.set(id, name);
      }
    }
    return m;
  }, [statusList]);

  const statusIdsKey = useMemo(
    () =>
      statusIds
        .slice()
        .sort((a, b) => a - b)
        .join(","),
    [statusIds.join(",")],
  );

  const rows = useMemo((): StatusDurationRow[] => {
    const payload = data ?? [];
    const filterSet = new Set(statusIds);
    const isFiltering = statusIds.length > 0;

    const groups = new Map<
      string,
      {
        statusId: number | null;
        statusName: string;
        sumDays: number;
        count: number;
        dealIds: Set<number>;
      }
    >();

    for (const deal of payload) {
      const dealId = deal?.deal_id;
      if (typeof dealId !== "number") continue;

      const history = Array.isArray(deal?.status_history)
        ? deal.status_history
        : [];
      for (const h of history) {
        const statusId = typeof h?.status_id === "number" ? h.status_id : null;
        if (isFiltering) {
          if (statusId == null) continue;
          if (!filterSet.has(statusId)) continue;
        }

        const days = toFiniteNumber(h?.duration_days ?? h?.days_in_status);
        if (days == null) continue;

        const inferredName =
          (typeof h?.status_name === "string" && h.status_name.trim() !== ""
            ? h.status_name
            : null) ??
          (statusId != null ? (statusNameById.get(statusId) ?? null) : null) ??
          "Unknown";

        const key =
          statusId != null
            ? `id:${statusId}`
            : `name:${String(inferredName).toLowerCase()}`;
        const existing = groups.get(key);

        if (!existing) {
          groups.set(key, {
            statusId,
            statusName: inferredName,
            sumDays: days,
            count: 1,
            dealIds: new Set([dealId]),
          });
        } else {
          existing.sumDays += days;
          existing.count += 1;
          existing.dealIds.add(dealId);
        }
      }
    }

    const computed = Array.from(groups.values()).map((g) => {
      const avgDays = g.count > 0 ? g.sumDays / g.count : 0;
      const key =
        g.statusId != null
          ? `id:${g.statusId}`
          : `name:${String(g.statusName).toLowerCase()}`;
      return {
        key,
        statusId: g.statusId,
        statusName: g.statusName,
        deals: g.dealIds.size,
        avgDays,
      };
    });

    computed.sort((a, b) => {
      const ao =
        a.statusId != null ? (statusOrderById.get(a.statusId) ?? 9999) : 9999;
      const bo =
        b.statusId != null ? (statusOrderById.get(b.statusId) ?? 9999) : 9999;
      if (ao !== bo) return ao - bo;
      return a.statusName.localeCompare(b.statusName);
    });

    return computed;
  }, [data, statusIdsKey, statusOrderById, statusNameById]);

  const leftRows = useMemo(
    () => rows.filter((r) => !isHiddenOnLeft(r.statusName)),
    [rows],
  );

  useEffect(() => {
    // If the selection is no longer present (e.g., due to filter change), clear it.
    if (!selectedStatusKey) return;
    if (leftRows.some((r) => r.key === selectedStatusKey)) return;
    setSelectedStatusKey(null);
  }, [leftRows, selectedStatusKey]);

  const statusSummaryColumnDefs: ColDef<StatusDurationRow>[] = useMemo(
    () => [
      { headerName: "Status", field: "statusName", width: 140, pinned: "left" },
      {
        headerName: "Avg days",
        field: "avgDays",
        width: 50,
        cellStyle: { textAlign: "right" as const },
        valueFormatter: (p) =>
          typeof p.value === "number" ? String(Math.round(p.value)) : "",
      },
      {
        headerName: "Deals",
        field: "deals",
        width: 50,
        cellStyle: { textAlign: "right" as const },
      },
    ],
    [],
  );

  const statusColumns = useMemo(() => {
    const payload = data ?? [];
    const filterSet = new Set(statusIds);
    const isFiltering = statusIds.length > 0;

    const seen = new Map<
      string,
      { key: string; statusId: number | null; statusName: string }
    >();

    for (const deal of payload) {
      const history = Array.isArray(deal?.status_history)
        ? deal.status_history
        : [];
      for (const h of history) {
        const statusId = typeof h?.status_id === "number" ? h.status_id : null;
        if (isFiltering) {
          if (statusId == null) continue;
          if (!filterSet.has(statusId)) continue;
        }

        const inferredName =
          (typeof h?.status_name === "string" && h.status_name.trim() !== ""
            ? h.status_name
            : null) ??
          (statusId != null ? (statusNameById.get(statusId) ?? null) : null) ??
          "Unknown";

        const key =
          statusId != null
            ? `id:${statusId}`
            : `name:${String(inferredName).toLowerCase()}`;
        if (!seen.has(key))
          seen.set(key, { key, statusId, statusName: inferredName });
      }
    }

    const cols = Array.from(seen.values());
    cols.sort((a, b) => {
      const ao =
        a.statusId != null ? (statusOrderById.get(a.statusId) ?? 9999) : 9999;
      const bo =
        b.statusId != null ? (statusOrderById.get(b.statusId) ?? 9999) : 9999;
      if (ao !== bo) return ao - bo;
      return a.statusName.localeCompare(b.statusName);
    });
    return cols;
  }, [data, statusIdsKey, statusOrderById, statusNameById]);

  const dealMatrixRowsAll = useMemo((): DealMatrixRow[] => {
    const payload = data ?? [];
    const filterSet = new Set(statusIds);
    const isFiltering = statusIds.length > 0;

    const byDeal = new Map<number, DealMatrixRow>();

    for (const deal of payload) {
      const dealId = deal?.deal_id;
      if (typeof dealId !== "number") continue;

      const dealName =
        typeof deal?.deal_name === "string" && deal.deal_name.trim() !== ""
          ? deal.deal_name
          : `Deal ${dealId}`;
      const row =
        byDeal.get(dealId) ??
        (() => {
          const next: DealMatrixRow = { dealId, dealName, statusDates: {} };
          byDeal.set(dealId, next);
          return next;
        })();

      const history = Array.isArray(deal?.status_history)
        ? deal.status_history
        : [];
      for (const h of history) {
        const statusId = typeof h?.status_id === "number" ? h.status_id : null;
        if (isFiltering) {
          if (statusId == null) continue;
          if (!filterSet.has(statusId)) continue;
        }

        const inferredName =
          (typeof h?.status_name === "string" && h.status_name.trim() !== ""
            ? h.status_name
            : null) ??
          (statusId != null ? (statusNameById.get(statusId) ?? null) : null) ??
          "Unknown";

        const key =
          statusId != null
            ? `id:${statusId}`
            : `name:${String(inferredName).toLowerCase()}`;

        const changedIso =
          typeof h?.changed_at === "string"
            ? h.changed_at
            : typeof h?.date_changed === "string"
              ? h.date_changed
              : null;
        if (!changedIso) continue;
        const changed = toValidDate(changedIso);
        if (!changed) continue;

        const existingIso = row.statusDates[key];
        const existing = existingIso ? toValidDate(existingIso) : null;
        if (!existing || changed.getTime() > existing.getTime()) {
          row.statusDates[key] = changedIso;
        }
      }
    }

    return Array.from(byDeal.values()).sort((a, b) =>
      a.dealName.localeCompare(b.dealName),
    );
  }, [data, statusIdsKey, statusNameById]);

  const dealMatrixRows = useMemo(() => {
    if (!selectedStatusKey) return dealMatrixRowsAll;
    return dealMatrixRowsAll.filter((r) =>
      Boolean(r.statusDates[selectedStatusKey]),
    );
  }, [dealMatrixRowsAll, selectedStatusKey]);

  const selectedStatusLabel = useMemo(() => {
    if (!selectedStatusKey) return null;
    const fromLeft = leftRows.find((r) => r.key === selectedStatusKey);
    if (fromLeft) return fromLeft.statusName;
    const fromCols = statusColumns.find((c) => c.key === selectedStatusKey);
    return fromCols?.statusName ?? selectedStatusKey;
  }, [selectedStatusKey, leftRows, statusColumns]);

  const handleExportToExcel = () => {
    setIsExporting(true);
    try {
      exportStatusAnalysisToExcel({
        leftRows,
        dealMatrixRows,
        statusColumns,
        statusFilter,
        selectedStatusLabel,
      });
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to export to Excel. Please try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const dealMatrixColumnDefs: ColDef<DealMatrixRow>[] = useMemo(() => {
    const base: ColDef<DealMatrixRow>[] = [
      {
        headerName: "Deal",
        field: "dealName",
        pinned: "left",
        width: 240,
        minWidth: 200,
        cellRenderer: (p: any) => {
          const dealId = p.data?.dealId;
          const label = p.value as string | undefined;
          if (typeof dealId !== "number") return label ?? "";
          return (
            <a
              href={`/deals/${dealId}`}
              style={{ color: "#1B7A63", textDecoration: "underline" }}
            >
              {label ?? `Deal ${dealId}`}
            </a>
          );
        },
      },
    ];

    const statusCols: ColDef<DealMatrixRow>[] = statusColumns.map((s) => {
      const col: ColDef<DealMatrixRow> = {
        headerName: s.statusName,
        colId: s.key,
        flex: 1,
        minWidth: 90,
        valueGetter: (p) => {
          const iso = p.data?.statusDates?.[s.key];
          if (!iso) return "";
          return formatDate(iso);
        },
        tooltipValueGetter: (p) => p.data?.statusDates?.[s.key] ?? "",
        cellStyle: {
          textAlign: "right" as const,
          fontVariantNumeric: "tabular-nums" as const,
        },
      };

      if (s.statusName === "Received Materials") {
        col.comparator = (
          _valueA: unknown,
          _valueB: unknown,
          nodeA: IRowNode<DealMatrixRow>,
          nodeB: IRowNode<DealMatrixRow>,
          isDescending: boolean,
        ) => {
          const ms = (iso: string | undefined) => {
            if (!iso) return null;
            const t = toValidDate(iso)?.getTime();
            return typeof t === "number" && !Number.isNaN(t) ? t : null;
          };
          const a = ms(nodeA?.data?.statusDates?.[s.key]);
          const b = ms(nodeB?.data?.statusDates?.[s.key]);
          if (a === null && b === null) return 0;
          if (a === null) return isDescending ? -1 : 1;
          if (b === null) return isDescending ? 1 : -1;
          return a - b;
        };
      }

      return col;
    });

    return [...base, ...statusCols];
  }, [statusColumns]);

  useEffect(() => {
    if (!dealGridApi) return;
    dealGridApi.sizeColumnsToFit?.();
  }, [dealGridApi, statusColumns.length]);

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: "0px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          Loading status analysis…
        </Typography>
      </Box>
    );
  }

  if (!data || rows.length === 0) {
    return (
      <Box sx={{ padding: "0px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <CustomMultiSelect
            value={statusFilter.map((s) => s.id)}
            onChange={(e) => {
              const selectedIds = e.target.value as (string | number)[];
              const selectedStatuses = (statusList ?? []).filter(
                (opt: Status): opt is Status => selectedIds.includes(opt.id),
              );
              setStatusFilter(selectedStatuses);
            }}
            options={statusList ?? []}
            placeholder="Select Status"
            labelKey="status"
            minWidth="200px"
          />
        </Box>
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          {statusIds.length > 0
            ? "No status history matched the selected status filter."
            : "No status history found to analyze."}
        </Typography>
        <ErrorDialog
          open={errorDialog.open}
          onClose={hideError}
          apiResponse={errorDialog.apiResponse}
          customMessage={errorDialog.customMessage}
          title={errorDialog.title}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mt: 2,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <CustomMultiSelect
          value={statusFilter.map((s) => s.id)}
          onChange={(e) => {
            const selectedIds = e.target.value as (string | number)[];
            const selectedStatuses = (statusList ?? []).filter(
              (opt: Status): opt is Status => selectedIds.includes(opt.id),
            );
            setStatusFilter(selectedStatuses);
          }}
          options={statusList ?? []}
          placeholder="Select Status"
          labelKey="status"
          minWidth="200px"
        />
        <CustomButton
          handleButtonPress={handleExportToExcel}
          isButtonDisabled={isExporting}
          text="Export to Excel"
          isLoading={isExporting}
          isLoadingText="Exporting..."
          solid={false}
          widthOverride="140px"
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography className="text-general-section-title">
            Status duration averages
          </Typography>
        </Box>

        <Box
          sx={{ display: "flex", gap: 2, alignItems: "stretch", width: "100%" }}
        >
          {/* Left: status summary */}
          <Box sx={{ width: 260, flexShrink: 0 }}>
            <Typography sx={{ fontSize: "12px", color: "#182737", mb: 0.75 }}>
              Click a status to filter deals
            </Typography>
            <div
              className="ag-theme-alpine"
              style={{ width: "100%", height: 650 }}
            >
              <AgGridReact
                rowData={leftRows}
                columnDefs={statusSummaryColumnDefs}
                defaultColDef={{
                  resizable: true,
                  filter: false,
                  sortable: false,
                }}
                rowHeight={30}
                headerHeight={45}
                suppressCellFocus={true}
                onRowClicked={(e) => {
                  const key = (e.data as StatusDurationRow | undefined)?.key;
                  if (!key) return;
                  setSelectedStatusKey((prev) => (prev === key ? null : key));
                }}
              />
            </div>
          </Box>

          {/* Right: deals x statuses matrix */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "12px", color: "#182737", mb: 0.75 }}>
              {selectedStatusKey
                ? `Showing ${dealMatrixRows.length} deals that hit the selected status`
                : `Showing ${dealMatrixRows.length} deals`}
            </Typography>
            <div
              className="ag-theme-alpine"
              style={{ width: "100%", height: 650 }}
            >
              <AgGridReact
                rowData={dealMatrixRows}
                columnDefs={dealMatrixColumnDefs}
                defaultColDef={{
                  resizable: true,
                  filter: false,
                  sortable: true,
                }}
                rowHeight={30}
                headerHeight={45}
                suppressCellFocus={true}
                onGridReady={(p) => {
                  setDealGridApi(p.api);
                  p.api.sizeColumnsToFit?.();
                }}
                onFirstDataRendered={(p) => p.api.sizeColumnsToFit?.()}
              />
            </div>
          </Box>
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
